import type { GlobalNaviDataV2, GlobalNaviProduct } from '@rt/global-navi/contract';
import { GlobalNavi, type OnNavigate } from '@rt/global-navi/react';
import { Domain as D } from 'auth-common/domain';
import {
  buildQantNaviFacts,
  hasOrganizationSettingsAccessForNavi,
  hasProjectSettingsAccessForNavi,
  toNaviRoleLabel,
} from 'auth-common/shared/globalNavi';
import { useCallback } from 'react';
import { useGlobalNaviGrantActions } from '../../hook/GlobalNavi/useGlobalNaviGrantActions';
import { useLatestProductByProjectId } from '../../hook/Product/latestProductByProjectId';
import { useLatestOrganizationId } from '../../hook/Project/latestOrganization';
import { useLatestProjectId } from '../../hook/Project/latestProject';
import { useAccount } from '../../provider/AccountProvider';
import { AuthState, useAuthContext } from '../../provider/AuthProvider';
import { useOrganization } from '../../provider/OrganizationProvider';
import { useProject } from '../../provider/ProjectProvider';
import type { GlobalNaviV2NavigationOptions, NavigationProps } from '../Navigation/types';

import Product = D.Object.Product;
import Project = D.Object.Project;

export type GlobalNaviV2Props = {
  productKey: Product.Key;
  options?: GlobalNaviV2NavigationOptions;
  subHeader?: NavigationProps['subHeader'];
};

/**
 * v2 GlobalNavi を auth context に結線する薄いブリッジ。
 *
 * - bootstrap-v2 で取得済みの `globalNavi` があればそれをそのまま `<GlobalNavi>` に渡す
 *   (backend が統合・権限解釈・並び順を確定済み)。
 * - 取得前 (初回ロード / bootstrap エラー fallback) は、authInfo から
 *   同形の GlobalNaviDataV2 を組み立てて渡す。これにより `<GlobalNavi>` 側は
 *   常に data-only API で描画でき、Karte/QANT の usage を同一に保てる。
 * - SPA 遷移は host が提供する `options.useOnMenuNavigate` を `onNavigate` 経由で接続する。
 */
export const GlobalNaviV2: React.FC<GlobalNaviV2Props> = ({ productKey, options, subHeader }) => {
  const { account } = useAccount();
  const { project, switchProject } = useProject();
  const { organizations } = useOrganization();
  const { setLatestProductByProjectId } = useLatestProductByProjectId();
  const { setLatestOrganizationId } = useLatestOrganizationId();
  const { setLatestProjectId } = useLatestProjectId();
  const { authState } = useAuthContext();
  const bootstrapData = AuthState.GetGlobalNavi(authState);
  const grantActions = useGlobalNaviGrantActions({ isEmployee: account.isEmployee });

  // SPA 遷移 hook。host が提供する場合のみ <a> の既定遷移を抑止する。
  // useOnMenuNavigate / useTransformProducts は内部で他 hook を呼ぶ前提なので
  // 条件分岐の外で必ず呼ぶ。
  const hostNavigate = options?.useOnMenuNavigate?.();
  const transformProducts = options?.useTransformProducts?.();
  const onNavigate: OnNavigate | undefined = hostNavigate
    ? (href, event) => {
        if (event.defaultPrevented) return false;
        return hostNavigate({ href, event });
      }
    : undefined;

  const data = bootstrapData ?? buildFallbackGlobalNaviData({ account, project, organizations });

  const onProjectSwitch = useCallback(
    ({ projectId, organizationId }: { projectId: string; organizationId: string }) => {
      if (project.id === projectId) return;
      setLatestProjectId(projectId as Project.Id);
      setLatestOrganizationId(organizationId as D.Object.Organization.Id);
      switchProject({ id: projectId as Project.Id });
    },
    [project.id, setLatestProjectId, setLatestOrganizationId, switchProject],
  );

  return (
    <GlobalNavi
      data={data}
      currentProduct={productKey}
      onNavigate={onNavigate}
      transformProducts={transformProducts}
      onProductChange={(nextProductKey) => {
        // 次回のAuthランディングは保存したproductKeyをQANTのパスに解決するため、
        // KARTE側だけ利用可能なSpeakは保存しない。移行設定による今回の遷移先とは独立に判定する。
        const isQantSpeakAccessible = data.current?.products.some(
          (product) => product.key === 'speak' && product.platform === 'qant' && product.accessible,
        );
        if (nextProductKey === 'speak' && !isQantSpeakAccessible) return;
        if (nextProductKey in Product.Map) {
          setLatestProductByProjectId((prev) => ({ ...prev, [project.id]: nextProductKey as Product.Key }));
        }
      }}
      onProjectSwitch={onProjectSwitch}
      grantActions={grantActions}
      subHeader={subHeader}
    />
  );
};

/**
 * bootstrap 経由で `globalNavi` が取れない場合に、authInfo から同形のデータを
 * 構築する。`accessible` は `Project.isProductAccessible` から、`facts` は
 * `buildQantNaviFacts` から算出する (backend の convertToQantNaviInputV2 と同じ計算)。
 *
 * Karte 側プロダクトは含まない。後続フェーズで bootstrap 経路が落ち着いたら
 * この fallback 自体を廃止できる。
 */
const buildFallbackGlobalNaviData = (params: {
  account: ReturnType<typeof useAccount>['account'];
  project: ReturnType<typeof useProject>['project'];
  organizations: ReturnType<typeof useOrganization>['organizations'];
}): GlobalNaviDataV2 => {
  const { account, project, organizations } = params;
  const organization = organizations.find((o) => o.projects.some((p) => p.id === project.id));
  const membership = project.membership;

  const products: GlobalNaviProduct[] = Product.List.map((p) => ({
    key: p.key,
    platform: 'qant' as const,
    accessible: membership
      ? Project.isProductAccessible({
          productSettings: project.productSettings,
          productKey: p.key,
          group: membership.group,
          account,
        })
      : Project.isProductEnabled({ productSettings: project.productSettings, productKey: p.key }),
    isAdmin: account.hasActiveSystemAdmin,
  }));

  const navOrganizations = organizations.map((org) => ({
    key: `qant:${org.id}`,
    name: org.name,
    ...(org.profileImageSrc ? { avatarUrl: org.profileImageSrc } : {}),
    hasOrgAdminAccess: hasOrganizationSettingsAccessForNavi(account, org),
    qantOrganizationId: org.id,
    projects: org.projects.map((p) => ({
      key: `qant:${p.id}`,
      name: p.name,
      qantProjectId: p.id,
      hasProjectSettingsAccess: hasProjectSettingsAccessForNavi(account, org, {
        group: p.group,
      }),
    })),
  }));

  return {
    selfPlatform: 'qant',
    user: {
      name: account.name,
      ...(account.profileImageSrc ? { avatarUrl: account.profileImageSrc } : {}),
      roleLabel: membership ? toNaviRoleLabel({ group: membership.group }) : 'システム管理者',
      grants: {
        isSystemAdminEligible: account.isSystemAdminEligible,
        hasActiveSystemAdmin: account.hasActiveSystemAdmin,
        isPiiEligible: account.isPiiEligible,
        hasActivePiiAccess: account.hasActivePiiAccess,
        isEmployee: account.isEmployee,
      },
    },
    organizations: navOrganizations,
    linkedAccounts: { qant: true, karte: true },
    requiresCrossPlatformLogout: true,
    current: {
      organization: { name: organization?.name ?? '' },
      project: { name: project.name },
      projectIds: { qant: project.id },
      organizationIds: { ...(organization ? { qant: organization.id } : {}) },
      products,
      ...(membership
        ? {
            facts: {
              qant: buildQantNaviFacts({
                account,
                project: { group: membership.group, featureFlags: project.featureFlags },
              }),
            },
          }
        : {}),
    },
  };
};
